import { getAuth } from 'firebase/auth'
import { publicConfig } from '@/config/publicConfig'
import { getApiKey } from '@/utils/apiKeyAuth'
import type { PrivateRequest } from './campaignLinks'
import type { QrRequest } from './model'

export class QrApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message)
    this.name = 'QrApiError'
  }
}
export const qrFetch: PrivateRequest = async (path, options) => {
  const key = getApiKey()
  const token = key || (await getAuth().currentUser?.getIdToken())
  if (!token) throw new QrApiError(401, 'Your session has expired. Sign in again.')
  const timeout = AbortSignal.timeout(options?.method === 'POST' ? 180_000 : 30_000)
  const signal = options?.signal ? AbortSignal.any([options.signal, timeout]) : timeout
  try {
    const response = await fetch(new URL(path, publicConfig.api.baseUrl), {
      method: options?.method ?? 'GET',
      body: options?.body,
      signal,
      cache: 'no-store',
      credentials: 'omit',
      redirect: path === '/v1/user/self' ? 'follow' : 'error',
      referrerPolicy: 'no-referrer',
      headers: {
        ...options?.headers,
        Authorization: `${key ? 'ApiKey' : 'Bearer'} ${token}`,
        'tenant-id': publicConfig.api.tenantId,
        Accept: options?.headers?.Accept ?? 'application/json',
      },
    })
    if (response.url && new URL(response.url).origin !== new URL(publicConfig.api.baseUrl).origin) {
      throw new Error('Unexpected API response origin')
    }
    return response
  } catch {
    throw new QrApiError(
      0,
      'The response could not be confirmed. Refresh the status before trying again.',
    )
  }
}
export const qrRequest: QrRequest = async <T>(
  path: string,
  options?: { method?: 'GET' | 'POST'; signal?: AbortSignal },
): Promise<T> => {
  const response = await qrFetch(path, options)
  let body: { data?: T; error?: unknown }
  try {
    body = await response.json()
  } catch {
    throw new QrApiError(
      response.status,
      'The server response could not be confirmed. Refresh the status.',
    )
  }
  if (!response.ok || body.error || body.data === undefined) {
    const message =
      typeof body.error === 'string'
        ? body.error
            .replace(/dora\.[\w.-]+/g, '[credential]')
            .replace(/[^\s@]+@[^\s@]+/g, '[email]')
        : 'Unable to load challenge status. Please refresh and try again.'
    throw new QrApiError(response.status, message)
  }
  return body.data
}
